/**
 * NZST timezone helpers — shared between frontend, worker, and scripts.
 *
 * Most of the app never needs explicit timezone conversion — Chart.js display,
 * tick labels, and axis formatting are all handled automatically by the
 * chartjs-adapter-luxon global zone setting (see main.ts). Use plain UTC epoch
 * milliseconds for Chart.js data points.
 *
 * These helpers exist for the cases where an explicit NZST conversion is
 * unavoidable:
 *
 * 1. Parsing API timestamps — the server returns NZST strings without a tz
 *    suffix (e.g. "2026-04-25T09:00:00"). We must tell Luxon the zone when
 *    parsing, otherwise it falls back to browser local time and a user outside
 *    NZ gets a wrong UTC epoch → their chart points land at the wrong position.
 *
 * 2. Building API request parameters and cache keys — "from" / "to" and cache
 *    keys must be expressed as NZST strings to match the server's date
 *    boundaries. A browser in UTC computing "30 days ago" in local time would
 *    request a window that is off by up to 12 hours relative to the NZ data.
 *
 * 3. Rainfall bucketing in session-cache — grouping tipping-bucket pulses into
 *    hourly totals must use NZST hour boundaries, not UTC, so that "1 pm"
 *    buckets align with what a NZ user would expect. UTC bucketing would split
 *    NZ midnight across two buckets.
 *
 * 4. flatpickr — the date picker is timezone-unaware and renders Date objects
 *    in browser local time. Without adjustment a UTC user would see the picker
 *    showing UTC time rather than NZST, and the selected value sent to the API
 *    would be wrong.
 */

import { DateTime } from "luxon";

export const TZ = "Pacific/Auckland";

// ── Time constants ────────────────────────────────────────────────────────────

export const HOUR_MS = 3_600_000;
export const DAY_MS = 24 * HOUR_MS;

// ── Conversion helpers ────────────────────────────────────────────────────────

/**
 * Parse an NZST ISO string from the API (no tz suffix) → UTC epoch ms.
 * Required for Chart.js data points (case 1 above).
 */
export function nzstToUtcMs(nzst: string): number {
  return DateTime.fromISO(nzst, { zone: TZ }).toMillis();
}

/**
 * UTC epoch ms → NZST ISO string "YYYY-MM-DDTHH:mm:ss".
 * Used for chart cursor → date picker handoff and building API "from"/"to"
 * strings from a UTC position (case 2 above).
 */
export function utcMsToNzst(ms: number): string {
  return DateTime.fromMillis(ms, { zone: TZ }).toISO({ includeOffset: false })!.slice(0, 19);
}

/**
 * Current time as an NZST ISO string "YYYY-MM-DDTHH:mm:ss".
 * Used for API "to" parameters and chart range bounds (case 2 above).
 */
export function nzstNow(): string {
  return DateTime.now().setZone(TZ).toISO({ includeOffset: false })!.slice(0, 19);
}

/**
 * N hours ago in NZST as "YYYY-MM-DDTHH:mm:ss".
 */
export function nzstHoursAgo(n: number): string {
  return DateTime.now().setZone(TZ).minus({ hours: n }).toISO({ includeOffset: false })!.slice(0, 19);
}

/**
 * N calendar days ago in NZST as "YYYY-MM-DDTHH:mm:ss".
 * Used for API "from" parameters (case 2 above).
 */
export function nzstDaysAgo(n: number): string {
  return DateTime.now().setZone(TZ).minus({ days: n }).toISO({ includeOffset: false })!.slice(0, 19);
}

/**
 * Today's date in NZST as "YYYY-MM-DD".
 * Used where a calendar date (not a datetime) is needed in NZST context,
 * e.g. filtering forecast data to exclude future days (case 2 above).
 */
export function nzstToday(): string {
  return DateTime.now().setZone(TZ).toFormat("yyyy-MM-dd");
}

/**
 * UTC epoch ms → NZST "YYYY-MM-DD HH:mm" for tooltip headers.
 * Chart.js tick callbacks receive raw UTC ms and format the string themselves.
 */
export function utcMsToNzstFull(ms: number): string {
  return DateTime.fromMillis(ms, { zone: TZ }).toFormat("yyyy-MM-dd HH:mm");
}

/**
 * Returns a Date suitable for flatpickr that displays the given NZST time
 * regardless of the browser's local timezone (case 4 above).
 *
 * flatpickr calls getHours()/getMinutes() (browser local time) to render its
 * display. Parsing a no-offset ISO string creates a Date at browser-local
 * midnight + the given time, so flatpickr always shows the NZST wall-clock
 * digits unchanged — a UTC user and an NZST user see the same time string.
 */
export function nzstForFlatpickr(nzst: string): Date {
  return new Date(nzst);
}

/** Current NZST time as a Date suitable for flatpickr (case 4 above). */
export function nzstNowForFlatpickr(): Date {
  const str = DateTime.now().setZone(TZ).toISO({ includeOffset: false })!.slice(0, 16);
  return new Date(str);
}

/** Current NZST time as "HH:mm" — for status bar and label display. */
export function nzstHHmm(): string {
  return DateTime.now().setZone(TZ).toFormat("HH:mm");
}
